import { memo } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { Badge, FlagIcon, ProgressBar, Tooltip } from 'components/common';
import { Dropdown, DropdownButton } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';
import { useEditorDispatch } from 'components/editor';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';
import type { EditorHeaderStateProps } from './EditorHeader';

type StateProps = Pick<
  EditorHeaderStateProps,
  | 'activeLanguageVersion'
  | 'previewType'
  | 'previewVersionCourseLanguage'
  | 'previewVersionCourseLanguageVersions'
  | 'translationReferenceLanguage'
>;

interface Props extends StateProps {
  translationProgressData: GetCourseTranslationProgressRes['progressByLanguage'];
}

const EditorHeaderLanguageVersions = ({
  activeLanguageVersion,
  previewType,
  previewVersionCourseLanguage,
  previewVersionCourseLanguageVersions,
  translationProgressData,
  translationReferenceLanguage
}: Props) => {
  const editorDispatch = useEditorDispatch();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const router = useRouter();

  const courseLanguage = previewVersionCourseLanguage || course.language;
  const courseLanguageVersions =
    previewVersionCourseLanguageVersions || course.languageVersions;

  const handleLanguageVersionChange = (languageKey: string) => {
    const query = { ...router.query };

    // remove language version info if switching to the default language
    if (languageKey === courseLanguage) {
      delete query.languageVersion;
      editorDispatch({
        type: 'updateContext',
        update: { editorActiveLanguageVersion: null }
      });
    } else {
      query.languageVersion = languageKey;
      editorDispatch({
        type: 'updateContext',
        update: { editorActiveLanguageVersion: languageKey }
      });
    }

    router.replace({ query });
  };

  const handleReferenceLanguageChange = (languageKey: string) => {
    editorDispatch({
      type: 'updateContext',
      update: { editorTranslationReferenceLanguage: languageKey }
    });
  };

  const buttonStyle = {
    backgroundColor: 'transparent',
    color: theme.textColorLight,
    fontSize: '1.3rem',
    fontWeight: '600',
    padding: 0
  };

  const flagIconStyles = { marginRight: 5, marginTop: -2 };

  const languageOptions = getAllLanguageOptionsByLangCode();

  // get active language version from url, fallback to default language
  const currentLanguageVersion =
    typeof router.query.languageVersion === 'string'
      ? router.query.languageVersion
      : courseLanguage;

  // get the full language label
  const currentLanguageObject = languageOptions[currentLanguageVersion];

  const referenceLanguageValue = translationReferenceLanguage || courseLanguage;

  const referenceLanguageObject = languageOptions[referenceLanguageValue];

  const dropdownTitle = currentLanguageObject ? (
    <>
      <FlagIcon
        lang={currentLanguageVersion}
        size="small"
        style={flagIconStyles}
      />
      {currentLanguageObject?.capitalizedName}
    </>
  ) : (
    currentLanguageVersion
  );

  const referenceDropdownTitle = referenceLanguageObject ? (
    <>
      <FlagIcon
        lang={referenceLanguageValue}
        size="small"
        style={flagIconStyles}
      />
      {referenceLanguageObject?.capitalizedName}
    </>
  ) : (
    referenceLanguageValue
  );

  // only offer fully translated language versions as reference options.
  // technically we could easily offer any incomplete language version as a reference option
  // as well, but this is an arbitrary decision to keep the UX simpler and more focused
  const fullyTranslatedReferenceOptions = [
    courseLanguage,
    ...Object.keys(courseLanguageVersions || {}).filter(
      (lang) =>
        lang !== activeLanguageVersion &&
        lang !== undefined &&
        lang !== null &&
        translationProgressData &&
        typeof translationProgressData[lang]?.totalProgress === 'number' &&
        translationProgressData[lang]?.totalProgress === 100
    )
  ];

  return (
    <div className="language-header-wrapper">
      <div className="dropdowns-row">
        {activeLanguageVersion &&
          fullyTranslatedReferenceOptions.length > 1 &&
          !previewType && (
            <>
              <Dropdown
                arrowStyle={{ marginLeft: 5 }}
                buttonStyle={buttonStyle}
                showArrow={true}
                styleContentButtons={false}
                title={referenceDropdownTitle}
                tooltip={messages.courseLanguageVersionsReferenceTranslation}
                underlineOnHover={true}
              >
                {fullyTranslatedReferenceOptions.map((languageKey) => {
                  const languageObject = languageOptions[languageKey];
                  return (
                    <DropdownButton
                      flag={languageKey}
                      isSelected={referenceLanguageValue === languageKey}
                      key={languageKey}
                      onClick={() => handleReferenceLanguageChange(languageKey)}
                      text={languageObject?.capitalizedName}
                    />
                  );
                })}
              </Dropdown>

              <span className="arrow-right">
                <Icon
                  icon="arrow-right"
                  style={{
                    fontSize: '1.1rem',
                    color: theme.textColor,
                    opacity: 0.8
                  }}
                />
              </span>
            </>
          )}

        <Dropdown
          arrowStyle={{ marginLeft: 5 }}
          buttonStyle={buttonStyle}
          showArrow={true}
          styleContentButtons={false}
          title={dropdownTitle}
          tooltip={messages.courseLanguageVersion}
          underlineOnHover={true}
        >
          {[courseLanguage, ...Object.keys(courseLanguageVersions || {})].map(
            (languageKey) => {
              const languageObject = languageOptions[languageKey];
              return (
                <DropdownButton
                  flag={languageKey}
                  isSelected={currentLanguageVersion === languageKey}
                  key={languageKey}
                  onClick={() => handleLanguageVersionChange(languageKey)}
                  text={
                    <div style={{ display: 'flex' }}>
                      <div style={{ minWidth: 60 }}>
                        {languageObject?.capitalizedName}
                      </div>

                      <div
                        style={{
                          marginRight: 10,
                          textAlign: 'right',
                          width: 120
                        }}
                      >
                        {languageKey === courseLanguage && (
                          <Badge
                            text={messages.courseLanguageVersionsDefault}
                            size="small"
                            style={{ marginLeft: 10 }}
                          />
                        )}

                        {languageKey !== courseLanguage &&
                          !previewType &&
                          typeof translationProgressData?.[languageKey]
                            ?.totalProgress === 'number' && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginLeft: 12
                              }}
                            >
                              <ProgressBar
                                color={
                                  translationProgressData[languageKey]
                                    ?.totalProgress < 100
                                    ? theme.warningStandaloneColor
                                    : theme.successStandaloneColor
                                }
                                horizontal={true}
                                label={
                                  translationProgressData[languageKey]
                                    ?.totalProgress + '%'
                                }
                                labelStyle={{
                                  fontSize: '1.2rem',
                                  textAlign: 'right',
                                  width: 40
                                }}
                                progress={
                                  translationProgressData[languageKey]
                                    ?.totalProgress / 100
                                }
                                progressStyle={{ marginBottom: 4, width: 50 }}
                              />
                            </span>
                          )}
                      </div>
                    </div>
                  }
                />
              );
            }
          )}
        </Dropdown>
      </div>

      {!previewType &&
        typeof translationProgressData?.[activeLanguageVersion]
          ?.totalProgress === 'number' && (
          <div className="progressbar-row">
            <Tooltip
              content={messages.courseLanguageVersionsTranslationProgress}
              childrenWrapInDiv={false}
            >
              <div>
                <ProgressBar
                  color={
                    translationProgressData[activeLanguageVersion]
                      ?.totalProgress < 100
                      ? theme.warningStandaloneColor
                      : theme.successStandaloneColor
                  }
                  horizontal={true}
                  label={
                    translationProgressData[activeLanguageVersion]
                      ?.totalProgress + '%'
                  }
                  progress={
                    translationProgressData[activeLanguageVersion]
                      ?.totalProgress / 100
                  }
                  progressStyle={{ width: 50 }}
                />
              </div>
            </Tooltip>
          </div>
        )}

      <style jsx>{`
        .language-header-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 12px;
        }
        .dropdowns-row {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 10px;
        }
        .arrow-right {
          display: flex;
          align-items: center;
        }
        .progressbar-row {
          display: flex;
          align-items: center;
        }
        @media (max-width: ${breakpoints.small}px) {
          .language-header-wrapper {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          .dropdowns-row {
            flex-direction: row;
            gap: 8px;
          }
          .progressbar-row {
            margin-left: 0;
            margin-top: 8px;
            width: 100%;
            max-width: 120px;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderLanguageVersions);
